import { useEffect, useState } from "react";
import { ListSearchSort } from "@/components/ListSearchSort";
import { sortByName, matchesQuery } from "@/lib/listSort";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Clock, Users, DollarSign, RefreshCw } from "lucide-react";

/**
 * Admin "Pending offers monitor" — one glance to see every unpaid package
 * offer across the platform, sorted most-at-risk-first. Coaches that keep
 * generating offers that never convert bubble to the top of the by-coach
 * roll-up so ops can follow up.
 */

const BAND_STYLE = {
  red: { border: "#FF4500", bg: "rgba(255,69,0,0.08)", label: "IMMINENT" },
  amber: { border: "#F59E0B", bg: "rgba(245,158,11,0.08)", label: "NUDGE" },
  green: { border: "#CCFF00", bg: "rgba(204,255,0,0.06)", label: "SAFE" },
};

export default function AdminPendingOffers() {
  const [data, setData] = useState({ pending: [], by_coach: [], totals: { count: 0, at_risk: 0, value_pending: 0 } });
  const [loading, setLoading] = useState(true);
  const [coachFilter, setCoachFilter] = useState("");
  const [bandFilter, setBandFilter] = useState("");
  const [poQuery, setPoQuery] = useState("");
  const [poSort, setPoSort] = useState("default");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/packages/pending-monitor");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load monitor");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = sortByName(data.pending.filter((r) => {
    if (coachFilter && r.coach_id !== coachFilter) return false;
    if (bandFilter && r.band !== bandFilter) return false;
    return matchesQuery(poQuery, r.student_name, r.coach_name, r.title);
  }), (r) => r.student_name, poSort);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pt-14 pb-16">
      <div className="max-w-6xl mx-auto px-6 space-y-6">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-[#CCFF00] mb-3" data-testid="back-to-admin">
            <ArrowLeft className="w-3 h-3" /> Admin console
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label-mono mb-2 text-[#FF4500]">
                <Clock className="w-3 h-3 inline mr-1" /> PENDING OFFERS MONITOR
              </div>
              <h1 className="h-display text-4xl sm:text-5xl tracking-tight">
                Every open package awaiting payment
              </h1>
            </div>
            <button
              onClick={load}
              className="px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5"
              data-testid="refresh-monitor"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-3xl">
            Sorted most-at-risk first. Offers auto-expire 14 days after creation — the countdown reflects that
            sweep. Use the by-coach panel to spot patterns (a coach with lots of amber/red offers may need help
            closing).
          </p>
        </div>

        {/* Totals row */}
        <div className="grid md:grid-cols-3 gap-3" data-testid="monitor-totals">
          <div className="card-surface p-4">
            <div className="label-mono text-[10px] text-[#A3A3A3]">TOTAL PENDING</div>
            <div className="h-display text-3xl text-[#CCFF00] mt-1" data-testid="totals-count">{data.totals.count}</div>
          </div>
          <div className="card-surface p-4">
            <div className="label-mono text-[10px] text-[#A3A3A3]">AT RISK (≤7 DAYS)</div>
            <div className="h-display text-3xl text-[#FF4500] mt-1" data-testid="totals-at-risk">{data.totals.at_risk}</div>
          </div>
          <div className="card-surface p-4">
            <div className="label-mono text-[10px] text-[#A3A3A3]">VALUE PENDING</div>
            <div className="h-display text-3xl text-white mt-1" data-testid="totals-value">£{Math.round(data.totals.value_pending)}</div>
          </div>
        </div>

        {/* By-coach rollup */}
        {data.by_coach.length > 0 && (
          <section className="card-surface p-5" data-testid="by-coach-panel">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#CCFF00]" />
              <div className="label-mono">By coach</div>
              <div className="ml-auto flex items-center gap-3 text-[10px] text-[#A3A3A3]" data-testid="funnel-legend">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{background:"#CCFF00"}} /> paid</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{background:"#F59E0B"}} /> pending</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{background:"#FF4500"}} /> declined</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{background:"#7f1d1d"}} /> expired</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="by-coach-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#A3A3A3] border-b border-[#262626]">
                    <th className="py-2 pr-3">Coach</th>
                    <th className="py-2 pr-3">Pending</th>
                    <th className="py-2 pr-3">At risk</th>
                    <th className="py-2 pr-3">Avg days left</th>
                    <th className="py-2 pr-3">Value</th>
                    <th className="py-2 pr-3">30d funnel</th>
                    <th className="py-2 pr-3">Conv %</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_coach.map((c) => {
                    const f = c.funnel || { offered: 0, paid: 0, declined: 0, expired: 0, still_pending: 0 };
                    const total = f.offered || 1; // avoid /0
                    const bar = (n, colour, label) => (
                      <div className="flex-1 min-w-[6px] group relative" title={`${label}: ${n}`}>
                        <div className="h-4" style={{ background: colour, opacity: n > 0 ? 1 : 0.15, width: `${Math.max(6, (n / total) * 100)}%`, minWidth: n > 0 ? "6px" : 0 }} />
                        <div className="text-[9px] text-[#A3A3A3] mt-0.5 label-mono">{n}</div>
                      </div>
                    );
                    return (
                      <tr key={c.coach_id} className="border-b border-[#262626] hover:bg-[#141414] align-top" data-testid={`by-coach-row-${c.coach_id}`}>
                        <td className="py-2 pr-3 text-white">{c.coach_name}</td>
                        <td className="py-2 pr-3">{c.pending_count}</td>
                        <td className="py-2 pr-3">
                          {c.at_risk > 0 ? <span className="text-[#FF4500] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{c.at_risk}</span> : <span className="text-[#A3A3A3]">0</span>}
                        </td>
                        <td className="py-2 pr-3">{c.avg_days_until_expiry ?? "—"}</td>
                        <td className="py-2 pr-3">£{Math.round(c.total_value_pending)}</td>
                        <td className="py-2 pr-3 min-w-[200px]" data-testid={`funnel-${c.coach_id}`}>
                          <div className="flex items-end gap-1">
                            {bar(f.paid, "#CCFF00", "paid")}
                            {bar(f.still_pending, "#F59E0B", "still pending")}
                            {bar(f.declined, "#FF4500", "declined")}
                            {bar(f.expired, "#7f1d1d", "expired")}
                          </div>
                          <div className="text-[9px] text-[#A3A3A3] mt-1 label-mono">
                            {f.offered} offered · 30d
                          </div>
                        </td>
                        <td className="py-2 pr-3" data-testid={`conv-${c.coach_id}`}>
                          {c.conversion_rate === null || c.conversion_rate === undefined ? (
                            <span className="text-[#A3A3A3]">—</span>
                          ) : (
                            <span
                              className="font-semibold"
                              style={{ color: c.conversion_rate >= 50 ? "#CCFF00" : c.conversion_rate >= 25 ? "#F59E0B" : "#FF4500" }}
                            >
                              {c.conversion_rate}%
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => setCoachFilter(coachFilter === c.coach_id ? "" : c.coach_id)}
                            className="text-[10px] uppercase tracking-wider text-[#CCFF00] hover:underline"
                            data-testid={`filter-coach-${c.coach_id}`}
                          >
                            {coachFilter === c.coach_id ? "clear" : "filter →"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Filter bar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="label-mono text-[10px] text-[#A3A3A3]">FILTER:</div>
          {["", "red", "amber", "green"].map((b) => (
            <button
              key={b || "all"}
              onClick={() => setBandFilter(b)}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${bandFilter === b ? "text-black" : "text-[#A3A3A3]"}`}
              style={bandFilter === b ? { background: b === "red" ? "#FF4500" : b === "amber" ? "#F59E0B" : b === "green" ? "#CCFF00" : "#FFFFFF", borderColor: "transparent" } : { borderColor: "#262626" }}
              data-testid={`band-filter-${b || "all"}`}
            >
              {b || "all"}
            </button>
          ))}
          {(coachFilter || bandFilter) && (
            <button onClick={() => { setCoachFilter(""); setBandFilter(""); }} className="text-[10px] text-[#A3A3A3] underline" data-testid="clear-filters">
              clear filters
            </button>
          )}
          <div className="text-[10px] text-[#A3A3A3] ml-auto" data-testid="filter-count">Showing {filtered.length} of {data.pending.length}</div>
        </div>

        {/* Pending list */}
        <section className="space-y-2" data-testid="pending-list">
          <ListSearchSort query={poQuery} onQuery={setPoQuery} sort={poSort} onSort={setPoSort} modes={["default", "az", "za"]} placeholder="Search player, coach or session…" testid="pending-offers" />
          {loading && <div className="text-sm text-[#A3A3A3] italic">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="card-surface p-6 text-center text-sm text-[#A3A3A3]" data-testid="empty-state">
              No pending offers match the current filter.
            </div>
          )}
          {filtered.map((p) => {
            const s = BAND_STYLE[p.band] || BAND_STYLE.green;
            return (
              <div
                key={p.id}
                className="p-4 border grid md:grid-cols-[100px_1fr_1fr_100px_140px] gap-3 items-center"
                style={{ borderColor: s.border, background: s.bg }}
                data-testid={`pending-row-${p.id}`}
              >
                <div>
                  <div className="label-mono text-[9px]" style={{ color: s.border }} data-testid={`band-${p.id}`}>{s.label}</div>
                  <div className="text-xs text-white mt-0.5">
                    {typeof p.days_until_auto_expiry === "number"
                      ? p.days_until_auto_expiry === 0 ? "today" : `${p.days_until_auto_expiry}d left`
                      : "—"}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#A3A3A3]">Coach</div>
                  <div className="text-sm text-white truncate">{p.coach_name}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#A3A3A3]">Student</div>
                  <div className="text-sm text-white truncate">{p.student_name}</div>
                  {p.student_email && <div className="text-[10px] text-[#A3A3A3] truncate">{p.student_email}</div>}
                </div>
                <div>
                  <div className="text-xs text-[#A3A3A3]">Sessions</div>
                  <div className="text-sm text-white">{p.sessions_total} × {p.sport}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#A3A3A3]">Price</div>
                  <div className="h-display text-xl text-[#CCFF00]" data-testid={`price-${p.id}`}>£{Math.round(p.price)}</div>
                </div>
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}
